import React from 'react';
import axios from 'helpers/axios';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { CompanyContext } from 'providers';
import { useCancelToken } from 'hooks';
import { buildFilter } from 'ui/components/pages/user/Dashboard/views/helper';

export const DashboardContext = React.createContext();

const initialState = {
  stats: {},
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SAVE_STATS':
      return {
        ...state,
        stats: action.stats,
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export const DashboardProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const { state: companyState } = React.useContext(CompanyContext);
  const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();

  const fetchStatistics = async (search = '', companyId = companyState.activeCompany) => {
    try {
      cancelPreviousRequest();
      const response = await axios.get(`/jobs/stats?$cid=${companyId}`, {
        cancelToken: newCancelToken(),
        params: {
          $search: typeof search === "object" ? buildFilter(search) : search,
        },
      });

      if (response.status === 200) {
        saveStatistics(response.data);
      }
    } catch (err) {
      if (isCancel(err)) return;
      console.error(err);
    }
  };

  const saveStatistics = (stats) => {
    dispatch({ type: 'SAVE_STATS', stats });
  };

  const updateJobs = (data, currentData, updateCallback) => {
    const updatedJobs = data || [];
    const customers = currentData.map((customer) => {
      const { jobs } = customer;
      jobs.forEach((job, index) => {
        for (const jobUpdate of updatedJobs) {
          if (job.id === jobUpdate.id) {
            customer.jobs[index].status = jobUpdate.status || job.status;
          }
        }
      });
      return customer;
    });
    updateCallback(customers);
  };

  const moveJob = (job, target, customers, updateCallback) => {
    const changes = {};
    if (job.customer !== target.customer.id) {
      changes.customer = target.customer.id;
    }
    if (job.category !== target.category.id) {
      changes.category = target.category.id;
    }

    if (_.isEmpty(changes)) return;

    const data = customers.map((customer) => {
      // remove job from current location
      if (customer.id === job.customer) {
        customer.jobs = customer.jobs.filter(({ id }) => job.id !== id);
      }
      // move job to target
      if (customer.id === target.customer.id) {
        customer.jobs.push({ ...job, ...changes });
      }
      return customer;
    });

    updateCallback(data);
    persistJob({ id: job.id, ...changes });
  };

  const persistJob = async (job) => {
    const { id, ...data } = job;

    try {
      const response = await axios.put(`/jobs/${id}`, data);

      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DashboardContext.Provider
      value={{
        state,
        dispatch,
        fetchStatistics,
        saveStatistics,
        updateJobs,
        moveJob,
        persistJob,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

DashboardProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
